import {
  Icon,
  MenuGroupContent,
  MenuLineSeparator,
  MenuOption,
} from "@smartsuite/react-ui/lib";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";

import { t } from "i18next";
import { useState } from "react";
import type {
  DynamicSection,
  DynamicSectionOption,
} from "../../../../../../../../types/line";
import { StyledIcon } from "./style";
import { getLabel } from "../../../../../../../../utils/conditions-coverter.hook";
import type { NumberPill } from "../../fields/NumberEditor/utils";
import { SolutionColorVariables } from "@smartsuite/types";

export const createPillAttrs = (
  option: DynamicSectionOption,
  selectedActionHash?: string,
  loopingMenuType?: string,
): PillNodeAttrs => ({
  icon: option.icon,
  title: option.labelSuffix
    ? `${option.label}: ${option.labelSuffix}`
    : option.label,
  value: JSON.stringify({
    value: option.outputId,
    target: loopingMenuType,
    hash: selectedActionHash,
  }),
});

export const ActionOptions: React.FC<{
  onNavigateBack: () => void;
  addPill: (attrs: PillNodeAttrs | NumberPill) => void;
  selectedActionMenu?: DynamicSection;
  goSubMenu?: (options: DynamicSectionOption[], label: string) => void;
  disabledOptionId?: string[];
  selectedActionHash?: string;
}> = ({
  onNavigateBack,
  addPill,
  selectedActionMenu,
  goSubMenu,
  disabledOptionId,
  selectedActionHash,
}) => {
  const [search, setSearch] = useState<string>("");

  const isEntireFieldSelected = (
    disabledOptionId: string[] | undefined,
    options: DynamicSectionOption[],
    option: DynamicSectionOption,
  ): boolean => {
    if (disabledOptionId === undefined) {
      return false;
    }
    const disabledOptionInSameGroup = disabledOptionId.filter((id) =>
      id.includes(option.label),
    );
    return disabledOptionInSameGroup.some((id) => {
      const option = options.find((option) => id === getLabel(option));
      return (
        option !== undefined &&
        option.labelSuffix === undefined &&
        option.groupFieldId !== undefined
      );
    });
  };

  const onOptionClick = (option: DynamicSectionOption): void => {
    if (option.nestedOptions?.length) {
      if (goSubMenu) {
        goSubMenu([option, ...option.nestedOptions], option.label);
      }
    } else {
      let pillTitle = option.label;
      if (option.labelSuffix != null) {
        pillTitle = `${option.label}: ${option.labelSuffix ?? ""}`;
      }

      addPill({
        icon: option.icon,
        title: pillTitle,
        value: JSON.stringify({
          value: option.outputId,
          target: "action",
          hash: selectedActionHash,
        }),
      });
    }
  };

  const availableOptions =
    selectedActionMenu?.options
      .filter((option) =>
        search.length > 0
          ? option.label
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase())
          : true,
      )
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const groupedOptions: DynamicSectionOption[] = [];
  availableOptions.forEach((option) => {
    if (option.groupFieldId !== undefined) {
      if (isEntireFieldSelected(disabledOptionId, availableOptions, option)) {
        return;
      }

      if (option.labelSuffix === undefined) {
        const isSelectedNested = disabledOptionId?.some((id) => {
          return id.includes(option.label);
        });
        if (isSelectedNested) {
          return;
        }
      }
      const group = groupedOptions.find(
        (group) => group.groupFieldId === option.groupFieldId,
      );
      if (group === undefined) {
        groupedOptions.push({
          ...option,
          nestedOptions: [],
          groupFieldId: option.groupFieldId,
          label: option.label,
        });
      } else {
        const groupOption = groupedOptions.find(
          (group) => group.groupFieldId === option.groupFieldId,
        );
        if (groupOption) {
          groupOption.nestedOptions?.push(option);
        }
      }
    } else {
      groupedOptions.push({
        ...option,
        nestedOptions: [],
        groupFieldId: "",
        label: option?.label ?? "",
      });
    }
  });

  return (
    <MenuGroupContent
      title={t("field-popups.action-popup", {
        name: selectedActionMenu?.name ?? "",
      })}
      onNavigateBack={onNavigateBack}
      search={Number(selectedActionMenu?.options.length) > 5}
      onSearchChange={(text) => setSearch(text)}
    >
      {groupedOptions
        .filter((option) => !option.contextSpecific)
        .map((option, index) => {
          return (
            <MenuOption
              submenuArrow={option.nestedOptions?.length ? "right" : undefined}
              key={option.outputId ?? index}
              onClick={() => onOptionClick(option)}
            >
              <>
                <StyledIcon>
                  <Icon
                    name={option.icon}
                    size="S"
                    color={SolutionColorVariables.sc1}
                  />
                </StyledIcon>
                {option.label}
              </>
            </MenuOption>
          );
        })}

      {groupedOptions.filter((option) => !option.contextSpecific).length > 1 &&
        groupedOptions.filter((option) => option.contextSpecific).length >
          0 && <MenuLineSeparator />}

      {groupedOptions
        .filter((option) => option.contextSpecific)
        .map((option, index) => {
          return (
            <MenuOption
              submenuArrow={option.nestedOptions?.length ? "right" : undefined}
              key={option.outputId ?? index}
              onClick={() => onOptionClick(option)}
            >
              <>
                <StyledIcon>
                  <Icon
                    name={option.icon}
                    size="S"
                    color={SolutionColorVariables.sc1}
                  />
                </StyledIcon>
                {option.label}
              </>
            </MenuOption>
          );
        })}
    </MenuGroupContent>
  );
};
